import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Trophy, TrendingUp } from 'lucide-react';
import Logo from '@/components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MyRankings() {
  const navigate = useNavigate();
  const [myRankings, setMyRankings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadMyRankings();
  }, []);

  const loadMyRankings = async () => {
    try {
      const response = await axios.get(`${API}/users/my-rankings`, { withCredentials: true });
      setMyRankings(response.data);
    } catch (error) {
      toast.error('Failed to load your rankings');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#007AFF]"></div>
          <p className="mt-4 text-white">Loading your rankings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button 
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <Logo size="lg" />
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-semibold mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
            My Rankings
          </h1>
          <p className="text-gray-400">Your performance across all ranking lists</p>
        </div>

        {myRankings.length === 0 ? (
          <Card className="bg-[#111111] border-[#27272A]">
            <CardContent className="py-12 text-center">
              <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">You haven't joined any ranking lists yet</p>
              <Button 
                onClick={() => navigate('/ranking-lists')}
                className="bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full font-bold"
              >
                Browse Ranking Lists
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {myRankings.map(ranking => (
              <Card 
                key={ranking.list_id}
                className="bg-[#111111] border-[#27272A] hover:border-[#007AFF]/50 transition-all cursor-pointer"
                onClick={() => navigate(`/ranking-lists/${ranking.list_id}`)}
                data-testid="my-ranking-card"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-white text-xl font-semibold mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {ranking.list_name}
                      </CardTitle>
                      <Badge className="bg-[#007AFF]/20 text-[#007AFF] rounded-full text-xs">
                        {ranking.match_format}
                      </Badge>
                    </div>
                    <TrendingUp className="w-8 h-8 text-[#007AFF]" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* ELO Score */}
                    <div className="text-center bg-[#0A0A0A] rounded-lg p-4">
                      <p className="text-sm text-gray-400 mb-1">Your ELO</p>
                      <p className="text-5xl font-black text-[#007AFF]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        {ranking.elo_score}
                      </p>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-[#0A0A0A] rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500 mb-1">Matches</p>
                        <p className="text-xl font-bold text-white">{ranking.matches_played}</p>
                      </div>
                      <div className="bg-[#0A0A0A] rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500 mb-1">Wins</p>
                        <p className="text-xl font-bold text-green-400">{ranking.wins}</p>
                      </div>
                      <div className="bg-[#0A0A0A] rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500 mb-1">Losses</p>
                        <p className="text-xl font-bold text-red-400">{ranking.losses}</p>
                      </div>
                    </div>

                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/ranking-lists/${ranking.list_id}`);
                      }}
                      className="w-full bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full font-bold"
                    >
                      View Leaderboard
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyRankings;
